import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl"></i>
          </span>
          <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
            粉色<span className="text-primary-500">视频</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`cursor-pointer rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/about"
            className={`cursor-pointer rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              isActive("/about")
                ? "bg-primary-100 text-primary-700"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            关于我们
          </Link>
        </nav>

        <div className="ml-auto hidden items-center md:flex">
          <div
            className={`flex items-center overflow-hidden rounded-full border transition-all duration-300 ${
              searchOpen
                ? "w-64 border-primary-300 bg-background-50 px-3"
                : "w-10 border-transparent bg-background-100 px-0"
            }`}
          >
            <button
              type="button"
              onClick={() => (searchOpen ? submitSearch() : setSearchOpen(true))}
              className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center text-foreground-600 hover:text-primary-500"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <input
              ref={inputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder="搜索电影、电视剧、综艺"
              className="h-10 w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 md:hidden"
          aria-label="菜单"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200/80 bg-background-50 px-4 py-4 md:hidden">
          <div className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder="搜索视频内容"
              className="h-10 w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </div>
          <nav className="grid grid-cols-2 gap-2">
            {[...navItems, { label: "关于我们", to: "/about" }].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cursor-pointer rounded-lg px-4 py-3 text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-800"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}